import React, { useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, shallowEqual } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { CLAIMS } from '../../../navigation/routes.constants';
import { useMixpanel } from '../../../../hooks';
import errorIcon from '../../../../assets/claimbot/submit-error.svg';
import successIcon from '../../../../assets/claimbot/submit-success.svg';
import {
  EV5,
  getMinutesFromInitialTime,
  getPMSByMarketChannelId,
} from '../../../../services';

import {
  REIMBURSEMENT_METHOD_TYPE_KEY_NAME,
  REIMBURSEMENT_METHOD_TYPE_SUBMIT,
} from '../claimbot.constants';
import { REIMBURSEMENT_METHOD_BY_NAME } from '../../../../constants';
import { getAttachmentSizeInMB } from '../claimbot.utils';

import './ClaimSubmitted.css';

const ClaimSubmitted = ({
  policyBrandName = '',
  selectedPolicy = {},
  success = false,
  toggleContactUsModalVisible = () => { },
}) => {
  const { t } = useTranslation('fileClaimbot');
  const history = useHistory();
  const { sendEvent } = useMixpanel();
  const icon = success ? successIcon : errorIcon;
  const title = success ? t('claimSubmitted.success.title')
    : t('claimSubmitted.error.title');

  const store = useSelector(({ claimsGeneric }) => ({
    claimsGeneric,
  }), shallowEqual);

  const { claimsGeneric: { userResponses, initialTime } } = store;

  const timeSpentBeforeSubmit = useMemo(() => {
    if (initialTime) {
      return getMinutesFromInitialTime(initialTime);
    }

    return null;
  }, [initialTime]);

  const attachmentInfo = useMemo(() => {
    if (userResponses.Attachments && userResponses.Attachments.value) {
      return Array.from(userResponses.Attachments.value).map((attachment) => ({
        filename: attachment.Filename,
        size: getAttachmentSizeInMB(attachment.size),
      }));
    }

    return [];
  }, [userResponses]);

  const policyNumber = useMemo(() => {
    if (userResponses.PolicyNumber) {
      return userResponses.PolicyNumber.value;
    }

    return '';
  }, [userResponses]);

  const pms = useMemo(() => {
    if (!selectedPolicy) {
      return '';
    }

    return getPMSByMarketChannelId(selectedPolicy.PolicyMarketingChannelId);
  }, [selectedPolicy]);

  const getReimbursementMethodFromUserResponse = useCallback(() => {
    const reimbursementResponse = userResponses[
      REIMBURSEMENT_METHOD_TYPE_KEY_NAME
    ];

    let response = '';

    if (reimbursementResponse) {
      const reimbursementType = reimbursementResponse.value;
      switch (reimbursementType) {
        case REIMBURSEMENT_METHOD_TYPE_SUBMIT.CHECK:
          response = REIMBURSEMENT_METHOD_BY_NAME.check;
          break;

        case REIMBURSEMENT_METHOD_TYPE_SUBMIT.ACH:
          response = REIMBURSEMENT_METHOD_BY_NAME.directDeposit;
          break;

        case REIMBURSEMENT_METHOD_TYPE_SUBMIT.CARE_CREDIT:
          response = REIMBURSEMENT_METHOD_BY_NAME.careCredit;
          break;

        default:
          response = `unknown type ${reimbursementType}`;
          break;
      }
    }

    return response;
  }, [userResponses]);

  const claimData = useMemo(() => {
    if (!userResponses) {
      return '';
    }

    const responses = Object.values(userResponses)
      .map((item) => item.value)
      .join(', ');

    return responses;
  }, [userResponses]);

  useEffect(() => {
    sendEvent(EV5({
      attachmentInfo,
      claimData,
      claimResult: success ? 'submitted' : 'error',
      pms,
      policyBrand: policyBrandName,
      policyNumber,
      reimbursementMethod: getReimbursementMethodFromUserResponse(),
      timeSpentBeforeSubmit,
    }));
  }, []);

  const description = useMemo(() => {
    if (success) {
      const onFinishPress = () => {
        history.push(CLAIMS);
      };

      return (
        <span>
          {t('claimSubmitted.success.toSeeTheStatus')}

          <button onClick={onFinishPress} type="button">
            {t('claimSubmitted.success.claims')}
          </button>

          {t('claimSubmitted.success.home')}
        </span>
      );
    }

    return (
      <span>
        {t('claimSubmitted.error.pleaseRetry')}

        <button onClick={toggleContactUsModalVisible} type="button">
          {t('claimSubmitted.error.contactUs')}
        </button>

        {t('claimSubmitted.error.forMoreInformation')}
      </span>
    );
  }, [success, t]);

  return (
    <div className="File-claimbot-claim-submitted-container">
      <img alt="" src={icon} />

      <h2>{title}</h2>

      {description}
    </div>
  );
};

export { ClaimSubmitted };
